import React, { useState, useEffect } from 'react';
import { X, Calendar, FileText, Loader2, Edit } from 'lucide-react';
import Swal from 'sweetalert2';
import { crmUpdateNote } from '../../../../services/vinculationCRMService';

export default function EditNoteModal({ open, onClose, onUpdated, note }) {
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    if (note && open) {
      setDate(note.date ? note.date.split('T')[0] : '');
      setTitle(note.title || '');
      setBody(note.body || '');
    }
  }, [note, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title && !body) {
      Swal.fire('Campos vacíos', 'Ingresa al menos un título o contenido', 'info');
      return;
    }

    try {
      setBusy(true);
      const updated = await crmUpdateNote(note.id, { 
        date: date || undefined, 
        title: title.trim() || undefined, 
        body: body.trim() || undefined 
      });
      const updatedNote = updated?.data || updated;
      onUpdated?.(updatedNote);
      Swal.fire({
        icon: 'success',
        title: 'Nota actualizada',
        timer: 1200,
        showConfirmButton: false,
      });
      onClose();
    } catch (err) {
      Swal.fire('Error', err?.message || 'No se pudo actualizar la nota', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!open || !note) return null;

  return (
    <div className="fixed inset-0 z-[90]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4 overflow-auto">
        <div className="w-full max-w-2xl my-4 rounded-2xl bg-white border shadow-2xl overflow-hidden flex flex-col max-h-[calc(100vh-2rem)]">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-[#0c3b66] to-[#0488AF] text-white flex items-center justify-between flex-none">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-lg bg-white/10 border border-white/20">
                <Edit className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-lg font-semibold">Editar nota</div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0" title="Cerrar">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto flex-1">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Fecha <span className="text-gray-400 text-xs">(opcional)</span></label>
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  title="Fecha asociada a la nota (puede ser diferente a la fecha de creación)"
                  className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                />
                <div className="mt-1 text-xs text-gray-500">
                  Fecha relevante para la nota (puede ser una fecha futura para recordatorios)
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Título <span className="text-gray-400 text-xs">(opcional)</span></label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ej: Recordatorio importante, Seguimiento de proyecto..."
                  title="Título breve y descriptivo de la nota"
                  className="w-full h-11 px-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contenido <span className="text-gray-400 text-xs">(opcional)</span></label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Escribe aquí el contenido detallado de la nota, recordatorios, observaciones, etc."
                  title="Contenido completo de la nota: detalles, recordatorios, observaciones importantes"
                  rows={6}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                <button 
                  type="button"
                  onClick={onClose} 
                  className="h-11 px-4 rounded-xl bg-[#C42251] text-white hover:bg-[#A01D45] transition-colors text-sm font-semibold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={busy || (!title && !body)}
                  className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-sm font-semibold"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Guardar cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

